import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import VenueAgreementRow from "./VenueAgreementRow";

const SAVE_DEBOUNCE_MS = 500;

let lastPlannedKey = 0;

export const plannedAgreementKey = () => {
  lastPlannedKey += 1;
  return `planned-agreement-${lastPlannedKey}`;
};

const sameFields = (posted, latest) => (
  posted.body === latest.body
    && (posted.required !== false) === (latest.required !== false)
    && (posted.fan_tag_name || "") === (latest.fan_tag_name || "")
);

const PlannedVenueAgreementsEditor = ({ agreements, onAgreementsChange }) => (
  <>
    {agreements.map((agreement, index) => (
      <VenueAgreementRow
        agreement={{ ...agreement, index }}
        onChange={(changed) => {
          // The rich editor reports focus and selection as changes too.
          if (sameFields(agreement, changed)) return;
          onAgreementsChange(agreements.map((existing, position) => (
            position === index
              ? { ...existing, body: changed.body, required: changed.required !== false, fan_tag_name: changed.fan_tag_name || "" }
              : existing
          )));
        }}
        onDelete={() => onAgreementsChange(agreements.filter((_existing, position) => position !== index))}
        key={agreement.key}
      />
    ))}
    <button
      className="team-btn-secondary"
      onClick={(e) => {
        e.preventDefault();
        onAgreementsChange([...agreements, { key: plannedAgreementKey(), body: "", required: true, fan_tag_name: "" }]);
      }}
      style={{ marginTop: "10px" }}
    >
      + Add Agreement
    </button>
  </>
);

const SavedVenueAgreementsEditor = ({
  csrfToken,
  teamId,
  venueOwnershipId,
  calendarEventId,
}) => {
  const [agreements, setAgreements] = useState([]);
  const debounceRef = useRef({});
  const creatingRef = useRef({});
  const draftRef = useRef({});

  const scopeKey = `${venueOwnershipId}:${calendarEventId || ""}`;
  const activeScopeRef = useRef(scopeKey);

  const collectionUrl = `/teams/${teamId}/venue_ownerships/${venueOwnershipId}/venue_agreements`;
  const headers = { "X-CSRF-TOKEN": csrfToken };

  const isStale = (requestScope, isCurrent) => (
    requestScope !== activeScopeRef.current || !isCurrent()
  );

  const showError = (error) => {
    const messages = error.response?.data;

    toast.error(Array.isArray(messages) ? messages.join(", ") : "Unable to save agreement", {
      position: toast.POSITION.TOP_CENTER,
      draggable: false,
      closeOnClick: false,
      autoClose: 5000,
      hideProgressBar: true,
    });
  };

  const reload = (requestScope, isCurrent = () => true) => (
    axios
      .get(collectionUrl, { params: { calendar_event_id: calendarEventId } })
      .then(({ data }) => {
        if (isStale(requestScope, isCurrent)) return;
        setAgreements(data.map((agreement, index) => ({ ...agreement, index })));
      })
      .catch((error) => {
        if (isStale(requestScope, isCurrent)) return;
        showError(error);
      })
  );

  useEffect(() => {
    activeScopeRef.current = scopeKey;
    creatingRef.current = {};
    draftRef.current = {};
    let ignore = false;
    reload(scopeKey, () => !ignore);

    return () => {
      ignore = true;
    };
  }, [venueOwnershipId, calendarEventId]);

  const debounce = (key, callback) => {
    if (debounceRef.current[key]) clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(callback, SAVE_DEBOUNCE_MS);
  };

  const paramsFor = (agreement) => ({
    venue_agreement: {
      body: agreement.body,
      required: agreement.required !== false,
      fan_tag_name: agreement.fan_tag_name || "",
      calendar_event_id: calendarEventId,
    },
  });

  const handleChange = (agreement) => {
    setAgreements((current) =>
      current.map((existing) =>
        (existing.id && existing.id === agreement.id) || existing.index === agreement.index
          ? { ...existing, ...agreement }
          : existing
      )
    );

    if (agreement.id) {
      debounce(`update-${agreement.id}`, () => {
        axios
          .put(`${collectionUrl}/${agreement.id}`, paramsFor(agreement), { headers })
          .catch(showError);
      });
      return;
    }

    draftRef.current[agreement.index] = agreement;

    if (creatingRef.current[agreement.index]) return;

    debounce(`create-${agreement.index}`, () => {
      const posted = draftRef.current[agreement.index];
      if (!posted) return;

      creatingRef.current[agreement.index] = true;

      axios
        .post(collectionUrl, paramsFor(posted), { headers })
        .then(({ data }) => {
          if (isStale(scopeKey, () => true)) return;

          setAgreements((current) =>
            current.map((existing) =>
              existing.index === agreement.index
                ? { ...existing, id: data.id }
                : existing
            )
          );

          const latest = draftRef.current[agreement.index];

          if (!sameFields(posted, latest)) {
            return axios
              .put(`${collectionUrl}/${data.id}`, paramsFor(latest), { headers })
              .catch(showError);
          }
        })
        .catch(showError)
        .then(() => {
          delete creatingRef.current[agreement.index];
        });
    });
  };

  const handleDelete = (agreement) => {
    if (!agreement.id) {
      reload(scopeKey);
      return;
    }

    axios
      .delete(`${collectionUrl}/${agreement.id}`, { headers })
      .then(() => reload(scopeKey))
      .catch((error) => {
        showError(error);
        reload(scopeKey);
      });
  };

  return (
    <>
      {agreements.map((agreement) => (
        <VenueAgreementRow
          agreement={agreement}
          onChange={handleChange}
          onDelete={handleDelete}
          key={agreement.id || `new-${agreement.index}`}
        />
      ))}
      <button
        className="team-btn-secondary"
        onClick={(e) => {
          e.preventDefault();
          setAgreements((current) => [
            ...current,
            { index: current.length, body: "", required: true, fan_tag_name: "" },
          ]);
        }}
        style={{ marginTop: "10px" }}
      >
        + Add Agreement
      </button>
    </>
  );
};

const VenueAgreementsEditor = (props) => (
  props.onAgreementsChange ? <PlannedVenueAgreementsEditor {...props} /> : <SavedVenueAgreementsEditor {...props} />
);

PlannedVenueAgreementsEditor.propTypes = {
  agreements: PropTypes.array.isRequired,
  onAgreementsChange: PropTypes.func.isRequired,
};

SavedVenueAgreementsEditor.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  teamId: PropTypes.number.isRequired,
  venueOwnershipId: PropTypes.number.isRequired,
  calendarEventId: PropTypes.number,
};

SavedVenueAgreementsEditor.defaultProps = {
  calendarEventId: null,
};

// Pass agreements and onAgreementsChange to edit a planned list in place, or
// csrfToken, teamId and venueOwnershipId to edit the saved agreements.
VenueAgreementsEditor.propTypes = {
  csrfToken: PropTypes.string,
  teamId: PropTypes.number,
  venueOwnershipId: PropTypes.number,
  calendarEventId: PropTypes.number,
  agreements: PropTypes.array,
  onAgreementsChange: PropTypes.func,
};

export default VenueAgreementsEditor;
